/**
 * Mirror of server-side document schema, used by the editor UI.
 * Keep in sync with server/src/content/document_schema.ts.
 */

export interface PackageDocument {
  version: 1;
  meta: {
    name: string;
    description?: string;
    duration_s: number;
    resolution: { w: number; h: number };
  };
  layout: Layout;
  scenes: Scene[];
}

export type LayoutKind =
  | 'fullscreen'
  | 'split-h'
  | 'split-v'
  | 'thirds-h'
  | 'thirds-v'
  | 'pip'
  | 'ticker-bottom'
  | 'freeform';

export type Layout =
  | { kind: 'fullscreen' }
  | { kind: 'split-h'; ratio: [number, number] }
  | { kind: 'split-v'; ratio: [number, number] }
  | { kind: 'thirds-h' }
  | { kind: 'thirds-v' }
  | { kind: 'pip'; position: 'tl' | 'tr' | 'bl' | 'br'; size_pct: number }
  | { kind: 'ticker-bottom'; ticker_h_pct: number }
  | { kind: 'freeform' };

export interface Scene {
  id: string;
  title?: string;
  duration_s: number;
  regions: Region[];
}

export interface Region {
  id: string;
  slot?: string;
  rect_pct?: { x: number; y: number; w: number; h: number };
  block: Block;
}

export type BlockKind =
  | 'text' | 'image' | 'video' | 'web' | 'clock'
  | 'weather' | 'rss' | 'youtube' | 'pdf' | 'custom-html';

export type Block =
  | TextBlock | ImageBlock | VideoBlock | WebBlock | ClockBlock
  | WeatherBlock | RssBlock | YouTubeBlock | PdfBlock | CustomHtmlBlock;

export interface TextBlock {
  kind: 'text';
  text: string;
  variant?: 'heading' | 'body' | 'marquee';
  font_size_px?: number;
  font_weight?: 400 | 500 | 600 | 700 | 800;
  color?: string;
  background?: string;
  align?: 'left' | 'center' | 'right';
  vertical_align?: 'top' | 'middle' | 'bottom';
  marquee_speed_pps?: number;
  padding_px?: number;
}
export interface ImageBlock { kind: 'image'; asset_id: string; fit?: 'cover' | 'contain' | 'fill'; background?: string; }
export interface VideoBlock { kind: 'video'; asset_id: string; loop?: boolean; muted?: boolean; fit?: 'cover' | 'contain' | 'fill'; }
export interface WebBlock { kind: 'web'; url: string; refresh_s?: number; }
export interface ClockBlock {
  kind: 'clock';
  style?: 'analog' | 'digital';
  timezone?: string;
  format?: string;
  show_date?: boolean;
  font_size_px?: number;
  color?: string;
  background?: string;
  align?: 'left' | 'center' | 'right';
}
export interface WeatherBlock { kind: 'weather'; location: string; units?: 'metric' | 'imperial'; provider?: 'open-meteo'; font_size_px?: number; }
export interface RssBlock { kind: 'rss'; url: string; item_count?: number; rotate_ms?: number; style?: 'card' | 'ticker'; }
export interface YouTubeBlock { kind: 'youtube'; video_id: string; is_playlist?: boolean; autoplay?: boolean; mute?: boolean; loop?: boolean; }
export interface PdfBlock { kind: 'pdf'; asset_id: string; page_duration_s?: number; first_page?: number; last_page?: number; }
export interface CustomHtmlBlock { kind: 'custom-html'; html: string; css?: string; js?: string; }

/** Slot names for each preset layout. */
export function slotsForLayout(layout: Layout): string[] {
  switch (layout.kind) {
    case 'fullscreen':    return ['main'];
    case 'split-h':       return ['left', 'right'];
    case 'split-v':       return ['top', 'bottom'];
    case 'thirds-h':      return ['left', 'middle', 'right'];
    case 'thirds-v':      return ['top', 'middle', 'bottom'];
    case 'pip':           return ['main', 'pip'];
    case 'ticker-bottom': return ['main', 'ticker'];
    case 'freeform':      return [];
  }
}

export function defaultDocument(name = 'Untitled'): PackageDocument {
  return {
    version: 1,
    meta: { name, duration_s: 10, resolution: { w: 1920, h: 1080 } },
    layout: { kind: 'fullscreen' },
    scenes: [{
      id: 'scene-1',
      duration_s: 10,
      regions: [{
        id: 'region-1',
        slot: 'main',
        block: {
          kind: 'text',
          text: 'Hello from IOTech Signage',
          variant: 'heading',
          font_size_px: 72,
          color: '#ffffff',
          background: '#0b1220',
          align: 'center',
          vertical_align: 'middle',
        },
      }],
    }],
  };
}

/** Make a fresh region for a given slot + kind, with sensible defaults. */
export function newBlock(kind: BlockKind): Block {
  switch (kind) {
    case 'text': return {
      kind: 'text', text: 'New text',
      variant: 'body', font_size_px: 48, color: '#ffffff', background: '#111827',
      align: 'center', vertical_align: 'middle',
    };
    case 'image': return { kind: 'image', asset_id: '', fit: 'contain', background: '#000' };
    case 'video': return { kind: 'video', asset_id: '', loop: true, muted: true, fit: 'contain' };
    case 'web':   return { kind: 'web', url: 'https://example.com' };
    case 'clock': return {
      kind: 'clock', style: 'digital', show_date: true,
      font_size_px: 96, color: '#ffffff', background: '#0b1220', align: 'center',
    };
    case 'weather': return { kind: 'weather', location: 'Walvis Bay', units: 'metric', provider: 'open-meteo' };
    case 'rss':     return { kind: 'rss', url: '', item_count: 5, rotate_ms: 5000 };
    case 'youtube': return { kind: 'youtube', video_id: '', autoplay: true, mute: true, loop: true };
    case 'pdf':     return { kind: 'pdf', asset_id: '', page_duration_s: 10 };
    case 'custom-html': return { kind: 'custom-html', html: '<div style="color:white;padding:2em">Custom HTML</div>' };
  }
}

export function blockLabel(kind: BlockKind): string {
  return ({
    'text': 'Text', 'image': 'Image', 'video': 'Video', 'web': 'Web page',
    'clock': 'Clock', 'weather': 'Weather', 'rss': 'RSS feed', 'youtube': 'YouTube',
    'pdf': 'PDF', 'custom-html': 'Custom HTML',
  } as Record<BlockKind, string>)[kind];
}
